import React from "react";
import { Button } from "@/components/ui/button";
import { Users, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import Toast from "@/pages/portal/components/Toast";

interface ReferenceModalProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    editingItem: any;
    onDelete: () => void;
    onSave: (data: any) => Promise<void> | void;
}

export default function ReferenceModal({
    isOpen,
    onOpenChange,
    editingItem,
    onDelete,
    onSave,
}: ReferenceModalProps) {
    const [firstName, setFirstName] = React.useState(editingItem?.firstName || "");
    const [lastName, setLastName] = React.useState(editingItem?.lastName || "");
    const [email, setEmail] = React.useState(editingItem?.email || "");
    const [phone, setPhone] = React.useState(editingItem?.phone || "");
    const [company, setCompany] = React.useState(editingItem?.company || "");
    const [jobTitle, setJobTitle] = React.useState(editingItem?.jobTitle || "");
    const [errors, setErrors] = React.useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [showValidationToast, setShowValidationToast] = React.useState(false);

    React.useEffect(() => {
        if (isOpen) {
            setFirstName(editingItem?.firstName || "");
            setLastName(editingItem?.lastName || "");
            setEmail(editingItem?.email || "");
            setPhone(editingItem?.phone || "");
            setCompany(editingItem?.company || "");
            setJobTitle(editingItem?.jobTitle || "");
            setErrors({});
            setShowValidationToast(false);
        }
    }, [isOpen, editingItem]);

    const handleSave = async () => {
        const newErrors: Record<string, string> = {};
        if (!firstName.trim()) newErrors.firstName = "First name is required.";
        if (!lastName.trim()) newErrors.lastName = "Last name is required.";
        if (!email.trim()) newErrors.email = "Email is required.";
        if (!company.trim()) newErrors.company = "Company is required.";
        if (!jobTitle.trim()) newErrors.jobTitle = "Job title is required.";
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setShowValidationToast(true);
            const fieldOrder = ["firstName", "lastName", "email", "company", "jobTitle"];
            const firstErrorField = fieldOrder.find((f) => newErrors[f]);
            if (firstErrorField) {
                setTimeout(() => {
                    document.getElementById(`ref-field-${firstErrorField}`)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
                }, 0);
            }
            return;
        }
        setIsSaving(true);
        setSaveError(null);
        try {
            await onSave({
                id: editingItem?.id,
                firstName,
                lastName,
                name: `${firstName} ${lastName}`.trim(),
                email,
                phone,
                company,
                jobTitle,
            });
            setErrors({});
            onOpenChange(false);
        } catch (error: any) {
            const msg = error?.response?.data?.detail || error?.message || "Failed to save. Please try again.";
            setSaveError(msg);
        } finally {
            setIsSaving(false);
        }
    };

    const fieldCls = (error?: string) =>
        `h-11 rounded-lg border bg-white text-sm font-normal focus-visible:ring-2 focus-visible:ring-[#001B44]/15 focus-visible:border-[#001B44] transition-colors ${
            error ? "border-red-400 focus-visible:ring-red-200/60" : "border-slate-800"
        }`;

    const SectionHeading = ({ children }: { children: React.ReactNode }) => (
        <div className="flex items-center gap-3 pb-1">
            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest whitespace-nowrap">
                {children}
            </span>
            <div className="flex-1 h-px bg-slate-100" />
        </div>
    );

    const FieldLabel = ({ htmlFor, children, required }: { htmlFor?: string; children: React.ReactNode; required?: boolean }) => (
        <label htmlFor={htmlFor} className="block text-sm font-medium text-slate-700 mb-1.5">
            {children}
            {required && <span className="text-rose-400 text-xs ml-1">*</span>}
        </label>
    );

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] rounded-2xl p-0 border-none shadow-2xl flex flex-col bg-white overflow-hidden">
                {showValidationToast && (
                    <Toast
                        message="Please fill in all required fields."
                        type="error"
                        mode="absolute"
                        onClose={() => setShowValidationToast(false)}
                        duration={4000}
                    />
                )}

                {/* ── Header ───────────────────────────────────────────── */}
                <DialogHeader className="px-6 pt-6 pb-5 shrink-0 border-b border-slate-100">
                    <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-xl bg-[#001B44]/[0.06] flex items-center justify-center text-[#001B44] shrink-0">
                            <Users className="w-5 h-5" />
                        </div>
                        <div>
                            <DialogTitle className="text-[17px] font-bold text-slate-900 leading-tight">
                                {editingItem ? "Edit Reference" : "Add Reference"}
                            </DialogTitle>
                            <p className="text-sm text-slate-400 mt-0.5">
                                {editingItem
                                    ? "Update your reference details."
                                    : "Add a professional reference to your profile."}
                            </p>
                        </div>
                    </div>
                </DialogHeader>

                {/* ── Scrollable body ───────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto px-6 py-6 space-y-7">

                    {/* Section 1 — Contact Details */}
                    <div className="space-y-4">
                        <SectionHeading>Contact Details</SectionHeading>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div id="ref-field-firstName">
                                <FieldLabel required>First Name</FieldLabel>
                                <Input
                                    value={firstName}
                                    onChange={(e) => { setFirstName(e.target.value); setErrors((p) => ({ ...p, firstName: "" })); }}
                                    placeholder="Enter first name"
                                    className={fieldCls(errors.firstName)}
                                />
                                {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                            </div>
                            <div id="ref-field-lastName">
                                <FieldLabel required>Last Name</FieldLabel>
                                <Input
                                    value={lastName}
                                    onChange={(e) => { setLastName(e.target.value); setErrors((p) => ({ ...p, lastName: "" })); }}
                                    placeholder="Enter last name"
                                    className={fieldCls(errors.lastName)}
                                />
                                {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                            </div>
                        </div>

                        <div id="ref-field-email">
                            <FieldLabel required>Email</FieldLabel>
                            <Input
                                type="email"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setErrors((p) => ({ ...p, email: "" })); }}
                                placeholder="name@example.com"
                                className={fieldCls(errors.email)}
                            />
                            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <FieldLabel>Phone</FieldLabel>
                            <Input
                                type="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="Enter phone number"
                                className={fieldCls()}
                            />
                        </div>
                    </div>

                    {/* Section 2 — Professional Details */}
                    <div className="space-y-4">
                        <SectionHeading>Professional Details</SectionHeading>

                        <div id="ref-field-company">
                            <FieldLabel required>Company / Organization</FieldLabel>
                            <Input
                                value={company}
                                onChange={(e) => { setCompany(e.target.value); setErrors((p) => ({ ...p, company: "" })); }}
                                placeholder="Enter company name"
                                className={fieldCls(errors.company)}
                            />
                            {errors.company && <p className="text-xs text-red-500 mt-1">{errors.company}</p>}
                        </div>

                        <div id="ref-field-jobTitle">
                            <FieldLabel required>Job Title</FieldLabel>
                            <Input
                                value={jobTitle}
                                onChange={(e) => { setJobTitle(e.target.value); setErrors((p) => ({ ...p, jobTitle: "" })); }}
                                placeholder="e.g. Professor"
                                className={fieldCls(errors.jobTitle)}
                            />
                            {errors.jobTitle && <p className="text-xs text-red-500 mt-1">{errors.jobTitle}</p>}
                        </div>
                    </div>
                </div>

                {/* ── Footer ───────────────────────────────────────────── */}
                <DialogFooter className="px-6 pt-4 pb-5 bg-white border-t border-slate-100 shrink-0">
                    <div className="flex items-center justify-between w-full gap-3">
                        <div className="shrink-0">
                            {editingItem && (
                                <Button
                                    variant="ghost"
                                    onClick={onDelete}
                                    className="text-red-500 hover:text-red-600 hover:bg-red-50 font-medium px-3 h-10 text-sm"
                                >
                                    <Trash2 className="w-4 h-4 mr-1.5" />
                                    Delete
                                </Button>
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            {saveError && (
                                <p className="text-xs text-red-500">{saveError}</p>
                            )}
                            <Button
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                className="text-slate-500 hover:text-slate-700 font-medium h-10 text-sm px-4"
                                disabled={isSaving}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="rounded-xl bg-[#001B44] hover:bg-[#002a6e] text-white font-semibold px-6 h-10 text-sm shadow-sm transition-colors"
                            >
                                {isSaving ? "Saving…" : "Save Reference"}
                            </Button>
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
